<script setup lang="ts">
import api from '~/api';
import uploadApi from '~/api/uploadRequest'; // 导入新的上传API模块
import axios, { AxiosError } from 'axios';
import Dialog from '@/components/Dialog.vue';
import FacePicker from '@/components/FacePicker.vue';
import type { Material, MaterialWithStatus, FaceDetectData, FaceSwapRes } from '~/types';
import appConfig from '~/app.config';
import { useDisplay } from 'vuetify';
import BottomSheet from '@/components/BottomSheet.vue';
import StatusOverlay from '@/components/StatusOverlay.vue';
import TipsDialog from '@/components/TipsDialog.vue';
const VideoEditorDialog = defineAsyncComponent(() => import('@/components/VideoEditorDialog.vue'));
import axiosRetry from 'axios-retry';
import { useUploadStore } from '~/stores/uploadStore';
import UploadProgress from '@/components/UploadProgress.vue';
import MaterialsHistory from '@/components/MaterialsHistory.vue';
import ResultFeedbackForm from '@/components/ResultFeedbackForm.vue';

const display = useDisplay();
const isMobile = computed(() => display.xs.value);
const auth = useAuthStore();
const comm = useCommStore();
const utils = useUtils();
const router = useRouter();
const localePath = useLocalePath();
const { t } = useI18n();
const { startLoading, stopLoading } = useLoadingStore();
const snackbar = useSnackbarStore();
const selectedFile = ref<File | null>(null);

// 拖拽相关状态
const isDragging = ref(false);
const dropZoneRef = ref<HTMLElement | null>(null);

const { $logError, $logInfo }: any = useNuxtApp();

const props = defineProps({
    matObj: {
        type: Object as PropType<Material>,
        required: false
    }
});

const faceDetectData = ref<FaceDetectData>(null);
const creditNum = ref<number>(null);
const freeRemaining = ref<number>(0);
const tool = ref<string>(null);
// 当前换脸模式：single 单人换脸，multiple 多人换脸
const currentSwapMode = ref<'single' | 'multiple'>('single');

// 素材文件选中后的emit
const emit = defineEmits(['file-selected']);
const previewUrl = ref(null);
const resultUrl = ref(null);
const originalFace = ref('');
// 保存换脸任务信息
const currentTaskId = ref<string>('');
const currentOrderId = ref<string>('');
// 控制反馈组件显示
const showFeedbackForm = ref<boolean>(true);
const mediaType = ref<'file' | 'image' | 'video'>('file');  // 'image', 'video', or 'file'
const orientation = ref<'landscape' | 'portrait'>('landscape');
// 轮训素材检测状态 计时器
let pollingDetectionTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
// 是否展示action
const showAction = ref<Boolean>(false);
// 大文件分块上传：用 UploadProgress（带暂停/续传），不并入模糊图 overlay
const isLargeUpload = computed(() => !!selectedFile.value && selectedFile.value.size > appConfig.LARGE_FILE_THRESHOLD);
// 当前的处理状态
const currStat = ref<'uploading' | 'uploadFailed' | 'analyzing' | 'analyzingFailed' | 'analyzingSuccess' | 'creating' | 'creatingFailed' | 'creatingSuccess'>(null);
// 是否展示 FacePicker组件
const facePickerVisible = ref(false);
// 是否展示处理视频时长模态

// 该组件有两种启用方式，1 是通过用户本地选择文件，
// 四个阶段：上传，分析，选脸，结果
// 监听素材对象的变化
watchEffect(() => {
    if (props.matObj) {
        emit('file-selected', true);
        const formatMap = {
            P001: appConfig.imageExt.filter(format => format.ext !== 'gif'),
            G001: [{ ext: 'gif', mime: 'image/gif' }],
            V001: appConfig.videoExt
        };
        let ext = props.matObj.format;
        let toolId = null;
        // @TIP: gif图，视频，图片都展示covert_url
        const url = [...appConfig.videoExt, { ext: 'gif', mime: 'image/gif' }].some(f => f.ext === ext) ?
            props.matObj.convert_url || props.matObj.url
            : props.matObj.url;
        previewUrl.value = url || "";
        determineMediaType(previewUrl.value);

        for (const [key, formats] of Object.entries(formatMap)) {
            const matchingFormat = formats.find((format) => format.ext === ext);
            if (matchingFormat) {
                toolId = key;
                break;
            }
        }

        onDetectSuccess(props.matObj.material_id, toolId);
    }
});

// 监听文件变化
watch(selectedFile, (newFile) => {
    if (!newFile) {
        previewUrl.value = null;
        return;
    }
    previewUrl.value = URL.createObjectURL(newFile);
    // 判断文件类型
    mediaType.value = newFile.type.startsWith('image') ? 'image' : newFile.type.startsWith('video') ? 'video' : 'file';
    if (mediaType.value === 'image') {
        const img = new Image();
        img.onload = () => {
            orientation.value = img.width > img.height ? 'landscape' : 'portrait';
        };
        img.src = previewUrl.value;
    } else if (mediaType.value === 'video') {
        const video = document.createElement('video');
        video.onloadedmetadata = () => {
            orientation.value = video.videoWidth > video.videoHeight ? 'landscape' : 'portrait';
        };
        video.src = previewUrl.value;
    }
});


// 判断fileUrl的媒体类型
function determineMediaType(url) {
    if (!url) return;

    const imageTest = new Image();
    imageTest.onload = () => {
        mediaType.value = 'image';
        orientation.value = imageTest.width > imageTest.height ? 'landscape' : 'portrait';
        imageTest.onload = null;
        imageTest.onerror = null;
    };
    imageTest.onerror = () => {
        // If image loading fails, try loading as video
        const videoTest = document.createElement('video');
        videoTest.onloadeddata = () => {
            mediaType.value = 'video';
            orientation.value = videoTest.videoWidth > videoTest.videoHeight ? 'landscape' : 'portrait';
            videoTest.onloadeddata = null;
            videoTest.onerror = null;
        };
        videoTest.onerror = () => {
            mediaType.value = 'file';
            videoTest.onloadeddata = null;
            videoTest.onerror = null;
        };
        videoTest.src = url;
    };
    imageTest.src = url;
}


onBeforeUnmount(() => {
    // stopFaceSwapResultPolling();
    if (previewUrl.value) {
        URL.revokeObjectURL(previewUrl.value);
    }
    if (resultUrl.value) {
        URL.revokeObjectURL(resultUrl.value);
    }
    if (pollingDetectionTimeoutTimer) {
        clearTimeout(pollingDetectionTimeoutTimer);
    }
});

const fileInput = ref<HTMLInputElement | null>(null);

// 重置拖拽状态的函数
const resetDragState = () => {
    isDragging.value = false;
};

// 确保在组件挂载后设置事件监听
onMounted(async () => {
    // 重置拖拽状态
    isDragging.value = false;
    
    // 添加全局拖拽事件处理
    document.addEventListener('dragend', resetDragState);
    document.addEventListener('drop', resetDragState);
    
    // 添加全局拖拽离开事件监听
    document.addEventListener('dragleave', (e: DragEvent) => {
        // 如果拖拽离开了document，则重置拖拽状态
        if (e.clientX <= 0 || e.clientY <= 0 || 
            e.clientX >= window.innerWidth || e.clientY >= window.innerHeight) {
            resetDragState();
        }
    });
    
    // 添加全局鼠标移动事件监听
    document.addEventListener('mousemove', (e: MouseEvent) => {
        if (!isDragging.value) return;
        
        // 如果没有拖拽区域引用，则直接重置状态
        if (!dropZoneRef.value) {
            resetDragState();
            return;
        }
        
        // 获取拖拽区域的位置信息
        const rect = dropZoneRef.value.getBoundingClientRect();
        
        // 如果鼠标不在拖拽区域内，则重置拖拽状态
        if (
            e.clientX < rect.left ||
            e.clientX > rect.right ||
            e.clientY < rect.top ||
            e.clientY > rect.bottom
        ) {
            resetDragState();
        }
    });
});


const openFile = () => {
    // 重置拖拽状态
    resetDragState();
    
    // 若未登录用户，跳转至登录页面
    if (!auth.isLogin) {
        router.push({
            path: '/login/google',
            query: { redirect: router.currentRoute.value.fullPath }
        });
        return;
    }
    // 选择文件前，先清空
    selectedFile.value = null;
    // 避免相同文件不触发onChange
    if (fileInput.value) {
        fileInput.value.value = null;
    }
    fileInput.value?.click();
};

// 处理用户文件
const handleFileChange = async (event: Event) => {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
        let file = target.files[0];
        // 订阅模式已下线;过去这里按 user_status === 'Free' 拦截上传 + 引导订阅,
        // 现在所有用户都是 Free,这种拦截会把买了积分的用户也挡在门外。
        // 实际的积分余额检查在后续 createFaceSwap 流程里做,这里直接放行即可。
        await processFile(file);
    }
};

// Handler for material selection from MaterialsHistory component
const onMaterialSelected = async (material: MaterialWithStatus) => {
    // Handle demo materials differently - they need to be downloaded first
    if (material.isDemo) {
        // Check user credits before processing demo material
        if (comm.userInfo?.credit_num <= 0) {
            setTimeout(() => {
                snackbar.showError('You are out of credits. Top up to keep going.');
            }, 1000);
            setTimeout(() => {
                router.push({
                    path: '/pricing',
                    query: { redirect: router.currentRoute.value.fullPath }
                });
            }, 3000);
            return;
        }
        
        try {
            startLoading('Loading demo material...');
            
            // Fetch the demo image as a blob
            const response = await fetch(material.url);
            const blob = await response.blob();
            
            // Create a File object from the blob
            const fileName = material.url.split('/').pop() || 'demo.webp';
            const file = new File([blob], fileName, { type: blob.type });
            
            // Process the file as if it was uploaded
            await processFile(file);
        } catch (error) {
            console.error('Failed to load demo material:', error);
            snackbar.showError('Failed to load demo material');
        } finally {
            stopLoading();
        }
    } else {
        // For non-demo materials, let the watchEffect handle it
        // This will trigger the props.matObj watcher
    }

};

// 处理文件的通用函数，用于处理拖拽和点击上传
const processFile = async (file: File) => {
    startLoading('loading...');

    const KB = 1024;
    const MB = 1024 * KB;
    const GB = 1024 * MB;
    // 设置最大文件大小
    let maxFileSize = 50 * MB;

    const validImageMime = appConfig.imageExt.map((format) => format.mime);
    const validVideoMime = appConfig.videoExt.map((format) => format.mime);

    if (file.type.startsWith('image')) {
        // maxFileSize = file.type === 'image/gif' ? 15 * MB : 10 * MB;
        if (!validImageMime.includes(file.type)) {
            stopLoading();
            showDialog({
                title: 'Unsupported Format',
                message: 'The uploaded image format is not supported. Please upload a supported format.',
                showIcon: true,
            });
            // 上报错误
            utils.reportError("文件上传校验失败", "Unsupported image format", {
                fileType: file.type,
                fileSize: file.size,
                fileName: file.name,
                validFormats: validImageMime,
            });
            return;
        }
    } else if (file.type.startsWith('video')) {
        maxFileSize = 1024 * MB;
        // 检查视频格式
        if (!validVideoMime.includes(file.type)) {
            stopLoading();
            showDialog({
                title: 'Wrong format',
                message: 'The uploaded video format is not supported. Please upload a supported format.',
                showIcon: true,
            });
            // 上报错误
            utils.reportError("文件上传校验失败", "Unsupported video format", {
                fileType: file.type,
                fileSize: file.size,
                fileName: file.name,
                validFormats: validVideoMime,
            });
            return;
        }

        // 检查视频时长（有限支持， 只对浏览器支持的格式进行时长检查）
        if (appConfig.videoExt.find(format => format.mime === file.type)?.browserSupport) {
            const duration = await utils.getVideoDuration(file);
                console.log(`视频时长: ${duration / 1000 / 1000}s`);
                // 如果超过最大时长，则提示用户
                if (duration > appConfig.maxVideoDuration) {
                    snackbar.showError(`Video duration exceeds ${(appConfig.maxVideoDuration / 60 / 1000 / 1000).toFixed(1)} minutes limit`);
                    // 上报时长超时
                    utils.reportError("视频时长超时", "Video duration exceeds limit", {
                        duration,
                        maxDuration: appConfig.maxVideoDuration,
                    });
                    // 关闭loading
                stopLoading();
                return;
            }
        }
    } else {
        stopLoading();
        showDialog({
            title: 'Wrong format',
            message: 'The uploaded format is not supported. Please choose the correct file format.',
            showIcon: true,
        });
        // 上报错误
        utils.reportError("文件上传校验失败", "Unsupported file format", {
            fileType: file.type,
            fileSize: file.size,
            fileName: file.name,
            validFormats: [...validImageMime, ...validVideoMime]
        });
        return;
    }

    // 检查文件大小
    if (file.size > maxFileSize) {
        stopLoading();
        showDialog({
            title: 'Size Limit Exceeded',
            message: `Your ${file.type.startsWith('image') ? 'image' : 'video'} is over the ${maxFileSize >= GB ? (maxFileSize / GB).toFixed(1) + ' GB' : (maxFileSize / MB).toFixed(0) + ' MB'} limit. Please upload a smaller file.`,
            showIcon: true,
        });
        // 上报错误
        utils.reportError("文件上传校验失败", "File size exceeds limit", {
            fileType: file.type,
            fileSize: file.size,
            fileName: file.name,
            maxAllowedSize: maxFileSize >= GB ? `${(maxFileSize / GB).toFixed(1)} GB` : `${(maxFileSize / MB).toFixed(0)} MB`,
        });
        return;
    }

    selectedFile.value = file;
    previewUrl.value = URL.createObjectURL(file);
    // 上传中开始展示底部状态
    currStat.value = 'uploading';
    showAction.value = true;
    emit('file-selected', true);

    // try {
    //     // 对图片进行处理
    //     if (file.type.startsWith('image')) {
    //         file = await utils.normalizeImage(file);
    //     }
    // } catch (error) {
    //     currStat.value = 'uploadFailed';
    //     stopLoading();
    //     console.error('Error checking video:', error);
    //     utils.reportError('前端文件上传校验失败', 'upload file failed', { errorIns: error });
    //     snackbar.showError(`${error.message}`);
    //     return;
    // }

    await uploadFile(file);
};

// 拖拽相关函数
const handleDragEnter = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    isDragging.value = true;
};

const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    isDragging.value = true;
};

const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // 获取事件相关元素
    const relatedTarget = e.relatedTarget as HTMLElement;
    const container = dropZoneRef.value;
    
    // 如果relatedTarget为null或者不在容器内，则认为真的离开了
    if (!container || !relatedTarget || !container.contains(relatedTarget)) {
        resetDragState();
    }
};

const handleDrop = async (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    resetDragState();
    
    // 若未登录用户，跳转至登录页面
    if (!auth.isLogin) {
        router.push({
            path: '/login/google',
            query: { redirect: router.currentRoute.value.fullPath }
        });
        return;
    }
    
    if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
        const file = e.dataTransfer.files[0];
        // 与 handleFileChange 同款:订阅下线后不能再用 user_status 拦截。
        await processFile(file);
    }
};

// 裁剪确认
const handleVideoConfirmTrim = async ({ start, end, videoFileUrl }) => {
    // console.log('start,', start / 1000 / 1000, ',end,', end / 1000 / 1000);
    const trimInterval = `${(start / 1000 / 1000).toFixed(2)},${(end / 1000 / 1000).toFixed(2)}`;
    // 裁剪逻辑    
    uploadFile(selectedFile.value, {
        trimInterval: trimInterval
    });
}

// 
const handleVideoEditorClose = () => {
    currStat.value = null;
    selectedFile.value = null;
    stopLoading();
    resetProgress();
}


// 获取签名 url
const getSignUrl = async (file: File, resumable: number = 0) => {
    const mimeType = file.type;

    // 根据 mime 类型查找对应的 ext
    const formatEntry = [...appConfig.imageExt, ...appConfig.videoExt].find(format => format.mime === mimeType);

    // if (!formatEntry) {
    //     throw new Error(`Unsupported format: ${mimeType}`);
    // }

    // 如果查找失败，则默认使用 mp4 作为扩展名
    const ext = formatEntry?.ext || 'mp4';
    // const ext = 'png';
    console.log(`Getting signed URL for file type: ${mimeType}, ext: ${ext}, resumable: ${resumable}`);
    
    // 使用新的上传API模块获取签名URL
    const resp = await api.getSignUrl(ext, resumable);
    console.log(`Got signed URL response:`, resp);
    return resp;
};

async function onDetectSuccess(materialId: string, toolId?: string) {
    // 计算toolId
    if (!toolId) {
        if (!selectedFile.value) {
            // If no file is selected and no toolId provided, we can't proceed
            snackbar.showError('No file or tool ID available');
            return;
        }
        const mimeType = selectedFile.value.type;
        toolId = mimeType === 'image/gif' ? 'G001' : mimeType.startsWith('image') ? 'P001' : mimeType.startsWith('video') ? 'V001' : null;
    }

    // If toolId is still not set, return
    if (!toolId) {
        snackbar.showError('Could not determine tool type');
        return;
    }
    // 检查用户积分是否足够，若不足，则跳转到pricing页面，不用进入选脸阶段
    if (comm.userInfo?.credit_num <= 0) {

        setTimeout(() => {
            snackbar.showError('Subscribe to unlock all features and enjoy exclusive offers!');
        }, 1000);
        setTimeout(() => {
            router.push({
                path: '/pricing',
                query: { redirect: router.currentRoute.value.fullPath, mat_id: materialId, tool_id: toolId }
            });
        }, 3000);
        return;
    }


    // 通过素材id + toolId获取素材详细信息
    startLoading('loading...');
    const material = await api.getMaterialDetail(materialId, toolId);

    // 更新素材url
    if (appConfig.videoExt.some(f => f.ext === material.format)) {
        previewUrl.value = material.url;
    }
    stopLoading();

    // 处理检测结果 - 兼容新旧格式
    let detectedFaces = [];
    // 如果 detect_url 为空，使用素材的原始 URL（用于 V1.0 单人模式）
    let detectUrl = material.detect_url || material.convert_url || material.url || '';
    let detectInfo = material.detect_info || null;

    // 判断是新格式还是旧格式
    if (detectInfo && Array.isArray(detectInfo) && detectInfo.length > 0) {
        const firstItem = detectInfo[0];

        // 新格式：包含 face 和 meta 字段
        if ('face' in firstItem && 'meta' in firstItem) {
            console.log('使用新格式 detect_info (FaceFusion), 检测到', detectInfo.length, '个人脸');
            detectedFaces = detectInfo.map((item: any) => ({
                index: item.meta.pos,
                imageUrl: item.face,
                bounds: item.meta.bounds,
                selectedFaceId: undefined
            }));
        }
        // 旧格式：包含 x, y, width, height 字段
        else if ('x' in firstItem || 'y' in firstItem) {
            console.log('使用旧格式 detect_info (坐标)');
            // 旧格式保持不变，在 FacePicker 中处理
        }
    }

    // 进入选脸阶段
    faceDetectData.value = {
        materialId,
        detectUrl,
        detectInfo,
        detectedFaces,
        duration: material.duration,  // 传递视频时长
        format: material.format  // 传递素材格式
    };
    creditNum.value = material.total_credit;
    freeRemaining.value = material.free_remaining ?? 0;
    tool.value = toolId;

    facePickerVisible.value = true;
}
// 轮询识别状态
const pollDetectionStatus = async (materialId: string, tid?: string) => {
    let retryCount = 0;
    const maxRetries = 1000;  // 设置最大重试次数，以防无限循环
    const retryInterval = 3000;  // 轮询间隔时间，单位为毫秒
    let taskId = tid;  // 初始化 taskId

    // 超时时间
    const timeout = 30 * 60 * 1000;
    // 当前轮询时间
    let currentPollTime = 0;



    const getDetectionStatus = async () => {
        currStat.value = 'analyzing';
        try {
            const response = await api.uploadDetectResult(materialId, taskId); // 带上taskId调用API
            taskId = response.task_id || taskId;  // 更新 taskId，如果新的响应中有 taskId，则使用新的

            switch (response.detect_status) {
                case 1:  // 已完成正常可用
                    await finishProgress();
                    currStat.value = 'analyzingSuccess';
                    onDetectSuccess(materialId);
                    break;
                case -1: // 识别失败
                    currStat.value = 'analyzingFailed';
                    break;
                case -2: // 已完成违规禁止
                    // 弹窗dialog提示
                    showDialog({
                        title: 'Inappropriate content detected',
                        message: 'Your upload may contain prohibited content, please upload another one.',
                        showIcon: true
                    });
                    break;
                case 0:  // 未完成
                    if (retryCount < maxRetries) {
                        retryCount++;
                        pollingDetectionTimeoutTimer = setTimeout(getDetectionStatus, retryInterval);
                    } else {
                        snackbar.showError('Identification process timeout');
                    }
                    break;
                default:
                    snackbar.showError('Unknown status code');
                    break;
            }
        } catch (error) {
            // 检查是否为NSFW内容审核失败（code 451）
            if (error.response?.data?.code === 451) {
                const message = error.response?.data?.message || '';
                if (message.toLowerCase().includes('nsfw') || 
                    message.toLowerCase().includes('inappropriate') ||
                    message.toLowerCase().includes('prohibited') ||
                    message.toLowerCase().includes('violate') ||
                    message.toLowerCase().includes('protection')) {
                    // 继续轮询，等待最终的 detect_status: -1
                    // 但在收到 -1 时，会显示 NSFW 提示
                    setTimeout(() => {
                        // 显示NSFW内容检测失败的提示
                        showDialog({
                            title: 'Inappropriate content detected',
                            message: 'Your upload may contain prohibited content, please upload another one.',
                            showIcon: true
                        });
                        currStat.value = 'analyzingFailed';
                    }, 1000); // 延迟1秒显示，给后端时间返回最终状态
                    return;
                }
            }
            
            // 判断超时的
            if (currentPollTime > timeout) {
                // stopLoading();
                snackbar.showError(error.response?.data?.message || error.response || error);
                utils.reportError(`getDetectionStatus 轮询超时${timeout}ms`, 'poll detection status failed', { errorIns: error });
                currStat.value = 'analyzingFailed';
                return;
            }
            currentPollTime += retryInterval;
            // 轮询
            getDetectionStatus();
        }
    };

    startFakeProgress(96);
    getDetectionStatus();
};


let timeoutId: ReturnType<typeof setTimeout> | null = null; // 保存 timeout 引用
// 轮询换脸结果
const pollFaceSwapResult = async ({ orderId, taskId, pollInterval, swapMode }: { orderId: string; taskId: string, pollInterval: number; swapMode?: 'single' | 'multiple' }) => {
    // 保存任务信息,用于反馈
    currentOrderId.value = orderId;
    currentTaskId.value = taskId;

    // 重置反馈组件显示状态(每次新的换脸都可以提交反馈)
    showFeedbackForm.value = true;

    // 保存换脸模式
    if (swapMode) {
        currentSwapMode.value = swapMode;
    }
    // 先关闭facePicker组件
    facePickerVisible.value = false;
    // 展示状态
    startFakeProgress(93, pollInterval);
    currStat.value = 'creating';

    // 展示底部按钮
    showAction.value = true;
    let retryCount = 0;
    const maxRetries = 4;
    const retryInterval = 60 * 1000;

    const timeout = 5 * 60 * 1000;
    // 当前轮询时间
    let currentPollTime = 0;

    const getFaceSwapStatus = async () => {
        try {
            currentPollTime += pollInterval;
            const result: FaceSwapRes = await api.fetchFaceSwapRes(orderId, taskId);
            if (result.task_status === 1) {
                // 成功
                console.log('Face swap completed successfully:', result.url);
                // 这里不能修改previewUrl，会导致重新触发watchEffect，应该是用resultUrl覆盖 previewUrl
                resultUrl.value = result.url;
                originalFace.value = result.face_url;
                await finishProgress();  // 请求完成，线性到 100%
                currStat.value = 'creatingSuccess';
            } else if (result.task_status === -1) {
                // 失败
                console.error('Face swap failed');
                currStat.value = 'creatingFailed';
                snackbar.showError('Network error, please try again');
            } else if (result.task_status === 0) {
                // 未完成
                if (retryCount < maxRetries) {
                    retryCount++;
                    timeoutId = setTimeout(getFaceSwapStatus, retryInterval);
                } else {
                    console.error('Face swap process timed out');
                }
            }
        } catch (error) {
            // 判断超时的
            if (currentPollTime > timeout) {
                snackbar.showError(error.response?.data?.message || error.response || error);
                utils.reportError(`getFaceSwapStatus 轮询超时${timeout}ms`, 'poll face swap result failed', { errorIns: error });
                currStat.value = 'creatingFailed';
                return;
            }
            currentPollTime += retryInterval;
            // 轮询
            getFaceSwapStatus();
        }
    };

    // 开始轮询
    timeoutId = setTimeout(getFaceSwapStatus, (pollInterval || 60) * 1000);
};

// 停止轮询
const stopFaceSwapResultPolling = () => {
    if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
        console.log('Polling stopped.');
    }
};

// 页面卸载或离开时停止轮询
onBeforeUnmount(() => {
    stopFaceSwapResultPolling();
});

const uploadStore = useUploadStore();
const uploadAbortController = ref<AbortController | null>(null);

// 添加一个变量保存当前的签名URL
const currentSignUrl = ref<string | null>(null);
const currentMaterialId = ref<string | null>(null);
const currentFileUrl = ref<string | null>(null);

// 修改 uploadFile 函数，保存签名URL和materialId
const uploadFile = async (file: File, options?: { trimInterval?: string }) => {
    if (!file) {
        snackbar.showError('Please select a file first');
        return;
    }

    try {
        const useChunkedUpload = file.size >  appConfig.LARGE_FILE_THRESHOLD;

        // 获取签名URL
        startLoading('loading...');
        const signUrlResp = await getSignUrl(file, useChunkedUpload ? 1 : 0);
        currentSignUrl.value = signUrlResp.sign_url; // 保存签名URL
        currentMaterialId.value = signUrlResp.material_id; // 保存materialId
        currentFileUrl.value = signUrlResp.file_url; // 保存file_url供断点续传使用

        if (useChunkedUpload) {
            // 大文件：使用分片上传
            // stopLoading(); // 获取完签名URL后立即关闭loading
            
            // 初始化上传状态
            uploadStore.initializeUpload(file);
            
            // 展示上传状态
            currStat.value = 'uploading';
            showAction.value = true;

            // 创建上传实例
            const uploadInstance = axios.create({
                timeout: 60000,
                validateStatus: function (status) {
                    return (status >= 200 && status < 300) || status === 308;
                }
            });

            // 配置重试策略
            axiosRetry(uploadInstance, {
                retries: 3,
                retryDelay: (retryCount) => retryCount * 1000,
                retryCondition: (error) => {
                    const isTimeoutError = error.code === 'ECONNABORTED' && error.message.includes('timeout');
                    return axiosRetry.isNetworkOrIdempotentRequestError(error) || isTimeoutError;
                }
            });

            await uploadAllChunks(signUrlResp, uploadInstance);

        } else {
            // 小文件：使用普通上传
            currStat.value = 'uploading';
            showAction.value = true;

            const response = await axios.put(signUrlResp.sign_url, file, {
                headers: {
                    'Content-Type': file.type || getMimeType(file.name),
                },
                // 允许跨域请求携带凭证
                withCredentials: false,
                timeout: 30 * 60 * 1000,
                onUploadProgress: (progressEvent) => {
                    // 更新上传进度UI
                    startFakeProgress(Math.min((progressEvent.loaded * 100) / progressEvent.total, 95));
                }
            });

            if (response.status === 200 || response.status === 201) {
                // 上传完成，写入数据库
                await api.confirmUpload({
                    material_id: currentMaterialId.value!,
                    url: signUrlResp.file_url,
                    format: file.name.split('.').pop()?.toLowerCase(),
                    size: file.size,
                });
                // 上传完成后的处理
                const detectCreated = await api.uploadDetectCreate({
                    materialId: currentMaterialId.value,
                    trimInterval: options?.trimInterval,
                    useFacefusion: false // 第一阶段检测，不获取人脸信息
                });
                const { material_id, task_id } = detectCreated;
                pollDetectionStatus(material_id, task_id);
            } else {
                throw new Error('Upload failed with status: ' + response.status);
            }
            stopLoading();
        }

    } catch (error) {
        stopLoading();
        $logError(error, 'uploadFile');
        utils.reportError('上传失败', 'upload error', { errorIns: error });
        // 定义一个局部变量，用于判断是否为大文件
        const useChunkedUpload = selectedFile.value && selectedFile.value.size > 10 * 1024 * 1024;
        if (useChunkedUpload) {
            uploadStore.setUploadStatus('failed');
        }
        currStat.value = 'uploadFailed';
        snackbar.showError(error?.message || 'Error uploading file');
    }
};

// 修改 resumeUpload 函数，移除不必要的 POST 请求，直接使用已存在的上传会话 URL。
const resumeUpload = async () => {
    if (selectedFile.value && currentSignUrl.value) {
        try {
            startLoading('loading...');
            // 创建上传实例
            const uploadInstance = axios.create({
                timeout: 60000,
                validateStatus: function (status) {
                    return (status >= 200 && status < 300) || status === 308;
                }
            });

            // 配置重试策略
            axiosRetry(uploadInstance, {
                retries: 3,
                retryDelay: (retryCount) => retryCount * 1000,
                retryCondition: (error) => {
                    const isTimeoutError = error.code === 'ECONNABORTED' && error.message.includes('timeout');
                    return axiosRetry.isNetworkOrIdempotentRequestError(error) || isTimeoutError;
                }
            });

            uploadStore.setUploadStatus('uploading');
            uploadAbortController.value = new AbortController();

            // 获取未完成的分片
            const pendingChunks = uploadStore.getPendingChunks();
            let allSuccess = true;

            // 从未完成的分片开始上传，直接使用已存在的上传会话URL
            for (let i = 0; i < pendingChunks.length; i++) {
                const chunk = pendingChunks[i];
                // 检查是否被取消
                if (!uploadAbortController.value) {
                    allSuccess = false;
                    break;
                }

                const isLastChunk = i === pendingChunks.length - 1;
                // 直接使用现有的 currentSignUrl.value，不需要重新初始化
                const success = await uploadChunk(chunk, isLastChunk, currentSignUrl.value, uploadInstance, true); // 添加一个参数表示是恢复上传
                if (!success && !isLastChunk) {
                    allSuccess = false;
                    break;
                }
            }

            if (allSuccess) {
                uploadStore.setUploadStatus('completed');
                // 上传完成，写入数据库
                await api.confirmUpload({
                    material_id: currentMaterialId.value!,
                    url: currentFileUrl.value!,
                    format: selectedFile.value?.name.split('.').pop()?.toLowerCase(),
                    size: selectedFile.value?.size,
                });
                // 上传完成后的处理
                const detectCreated = await api.uploadDetectCreate({
                    materialId: currentMaterialId.value
                });
                const { material_id, task_id } = detectCreated;
                pollDetectionStatus(material_id, task_id);
            } else {
                uploadStore.setUploadStatus('failed');
                snackbar.showError('Upload failed. Please retry.');
            }
            stopLoading();
        } catch (error) {
            snackbar.showError(error?.message || 'Error resuming upload');
            uploadStore.setUploadStatus('failed');
            stopLoading();
        }
    } else {
        // 如果没有保存的签名URL，则需要重新开始上传
        snackbar.showError('Upload session expired. Please start a new upload.');
        await uploadFile(selectedFile.value);
    }
};

// 修改 uploadAllChunks 函数
const uploadAllChunks = async (signUrlResp: any, uploadInstance: any) => {
    try {
        uploadStore.setUploadStatus('uploading');
        uploadAbortController.value = new AbortController();

        const pendingChunks = uploadStore.getPendingChunks();
        let allSuccess = true;

        // 获取上传会话URL
        let uploadSessionUrl = signUrlResp.sign_url;
        
        // 记录上传会话URL，用于断点续传
        currentSignUrl.value = uploadSessionUrl;

        // 串行上传分块
        for (let i = 0; i < pendingChunks.length; i++) {
            const chunk = pendingChunks[i];
            // 检查是否被取消
            if (!uploadAbortController.value) {
                allSuccess = false;
                break;
            }

            const isLastChunk = i === pendingChunks.length - 1;
            const success = await uploadChunk(chunk, isLastChunk, uploadSessionUrl, uploadInstance);
            if (!success) {
                allSuccess = false;
                break;
            }
        }

        if (allSuccess) {
            uploadStore.setUploadStatus('completed');
            // 上传完成，写入数据库
            await api.confirmUpload({
                material_id: currentMaterialId.value!,
                url: signUrlResp.file_url,
                format: selectedFile.value?.name.split('.').pop()?.toLowerCase(),
                size: selectedFile.value?.size,
            });
            // 上传完成后的处理
            const detectCreated = await api.uploadDetectCreate({
                materialId: currentMaterialId.value
            });
            const { material_id, task_id } = detectCreated;
            pollDetectionStatus(material_id, task_id);
        } else {
            uploadStore.setUploadStatus('failed');
            snackbar.showError('Upload failed. Please retry.');
        }
        stopLoading();
    } catch (error) {
        console.error('Error in uploadAllChunks:', error);
        stopLoading();
        utils.reportError('上传失败', 'upload error', { errorIns: error });
        currStat.value = 'uploadFailed';
        uploadStore.setUploadStatus('failed');
        snackbar.showError('Upload failed. Please retry.');
    }
};

// 添加暂停上传功能
const pauseUpload = () => {
    if (uploadAbortController.value) {
        uploadAbortController.value.abort();
        uploadAbortController.value = null;
        uploadStore.setUploadStatus('paused');
    }
};

// 添加重试上传功能
const retryUpload = async () => {
    if (selectedFile.value) {
        await resumeUpload();
    }
};

// 修改 uploadChunk 函数，添加 isResume 参数
const uploadChunk = async (chunk: ChunkState, isLastChunk: boolean = false, signUrl: string, uploadInstance: any, isResume: boolean = false) => {
    const chunkData = selectedFile.value.slice(chunk.start, chunk.end);
    
    // 修改请求头，确保格式正确
    const headers = {
        'Content-Type': 'application/octet-stream',
        'Content-Range': `bytes ${chunk.start}-${chunk.end - 1}/${selectedFile.value.size}`,
    };

    // 为非最后一个分块创建带重试的实例
    const chunkUploadInstance = isLastChunk ? axios.create({
        timeout: 60000,
        validateStatus: function (status) {
            return (status >= 200 && status < 300) || status === 308;
        }
    }) : uploadInstance;

    try {
        uploadStore.updateChunkStatus(chunk.index, 'uploading');
        
        // 只有在非恢复上传且是第一个分块时，才需要初始化上传会话
        if (!isResume && chunk.index === 0) {
            try {
                // 初始化可恢复上传会话
                const initResponse = await axios.post(signUrl, null, {
                    headers: {
                        'Content-Type': 'application/octet-stream',
                        'x-goog-resumable': 'start'
                    }
                });
                
                // 从响应头中获取上传会话URL
                const uploadUrl = initResponse.headers.location;
                if (!uploadUrl) {
                    throw new Error('Failed to get upload session URL');
                }
                
                // 保存上传会话URL供后续分块使用
                currentSignUrl.value = uploadUrl;
            } catch (error) {
                console.error('Error initializing upload session:', error);
                uploadStore.updateChunkStatus(chunk.index, 'failed');
                return false;
            }
        }

        // 统一的上传逻辑，适用于所有分块
        const response = await chunkUploadInstance.put(currentSignUrl.value, chunkData, {
            headers,
            timeout: 30 * 60 * 1000,
            withCredentials: false,
            onUploadProgress: (progressEvent) => {
                const percentCompleted = (progressEvent.loaded * 100) / (chunk.end - chunk.start);
                uploadStore.updateChunkProgress(chunk.index, percentCompleted);
            },
            signal: uploadAbortController.value?.signal
        });

        if (response.status === 308 || response.status === 200) {
            uploadStore.updateChunkStatus(chunk.index, 'completed');
            return true;
        }
        return false;

    } catch (error) {
        if (error.name === 'AbortError') {
            uploadStore.updateChunkStatus(chunk.index, 'pending');
            return false;
        } else {
            uploadStore.updateChunkStatus(chunk.index, 'failed');
            $logError(`Chunk ${chunk.index} upload failed:`, error);
            return false;
        }
    }
};

function showAgainToggle() {
    comm.setAppInfo({ canShowTipsPopup: !comm.appInfo.canShowTipsPopup });
}

async function onReCreate() {
    resultUrl.value = null;
    facePickerVisible.value = true;
    // await finishProgress();  // 请求完成，线性到 100%
    // 状态更新
    currStat.value = 'analyzingSuccess';
}

// 返回上传阶段
function goBackToUpload() {
    // 清除轮询定时器
    if (pollingDetectionTimeoutTimer) {
        clearTimeout(pollingDetectionTimeoutTimer);
        pollingDetectionTimeoutTimer = null;
    }

    // 重置所有组件内部状态
    facePickerVisible.value = false;
    currStat.value = null;
    showAction.value = false;
    selectedFile.value = null;
    previewUrl.value = null;
    resultUrl.value = null;
    faceDetectData.value = null;
    creditNum.value = null;
    tool.value = null;
    originalFace.value = '';
    mediaType.value = 'file';
    currentSwapMode.value = 'single'; // 重置换脸模式为单人模式
    resetProgress();
    emit('file-selected', false);

    // 统一清空全局 matObj 状态（不管是否存在）
    // 这样确保组件回到初始上传状态
    const matObj = useState<MaterialWithStatus>('matObj');
    if (matObj.value) {
        matObj.value = null;
    }
}

const dialogVisible = ref(false);
const dialogTitle = ref('');
const dialogMessage = ref('');
const dialogShowIcon = ref(false);

// 显示弹窗
function showDialog({
    title = '',
    message = '',
    showIcon = false,
    visible = true
}) {
    dialogTitle.value = title;
    dialogMessage.value = message;
    dialogShowIcon.value = showIcon;
    dialogVisible.value = visible;
}

// 隐藏并重置弹窗
function hideDialog() {
    dialogVisible.value = false;
    dialogTitle.value = '';
    dialogMessage.value = '';
    dialogShowIcon.value = false;
}

// tips 弹窗开关
const tipsDialogVisible = ref(false);
// 是否展示弹窗底部的按钮
const showBottomBtn = ref(true);
// 是否展示视频编辑弹窗
const isShowVideoEditorDialog = ref(false);
// 下次是否展示
const canShowTipsPopup = computed(() => comm.appInfo.canShowTipsPopup);
const { progress, enableTransition, startFakeProgress, finishProgress, resetProgress } = useFakeProgress();

const openPopup = (type: number) => {
    showBottomBtn.value = type === 1;
    tipsDialogVisible.value = true;
};

const handleTipsDialogConfirm = () => {
    openFile();
};

// 添加跳转到My Upload页面的函数
const goToMyCreations = () => {
    router.push(localePath('/my/creations'));
};

// 处理用户反馈提交成功
const onFeedbackSubmitted = (feedbackId: string) => {
    console.log('用户反馈提交成功:', feedbackId);
};

// 处理反馈组件关闭
const onFeedbackClose = () => {
    showFeedbackForm.value = false;
};

</script>


<template>
    <!-- empty status  -->
    <template v-if="!props?.matObj?.url && !selectedFile">
        <v-container 
            class="w-full m-auto flex flex-col items-center justify-center py-10 bg-neutral-900 rounded-2xl border-2 border-neutral-700 border-dashed lg:min-h-[553px]"
            :class="{ 'border-primary border-solid': isDragging }"
            @dragenter="handleDragEnter"
            @dragover="handleDragOver"
            @dragleave="handleDragLeave"
            @drop="handleDrop"
        >
            <img class="max-h-[50px] h-[50px] md:h-[50px]" src="/icon/upload_file.svg" alt="">
            <div class="my-4 text-base font-semibold text-center title md:text-xl lg:text-2xl">
                {{ isDragging ? t('components.faceSwap.dropFileHere') : t('components.faceSwap.uploadTitle') }}
            </div>
            <div class="flex flex-col justify-center items-center text-sm font-normal text-center restrict text-neutral-500 md:text-base">
                <div>{{ t('components.faceSwap.videoLimit') }}</div>
                <div>{{ t('components.faceSwap.photoLimit', { formats: appConfig.imageExt.filter(format => format.ext !== 'gif').map(format => format.ext).join(', ') }) }}</div>
                <div>{{ t('components.faceSwap.gifLimit') }}</div>
            </div>
            <input type="file" @change="handleFileChange" style="display: none;" ref="fileInput" />
            <v-btn @click="canShowTipsPopup ? openPopup(1) : openFile()" color="primary" class="mx-auto mt-6 w-[300px]" size="large" style="color: #000 !important;" prepend-icon="mdi-cloud-upload">
                <span>{{ t('components.faceSwap.uploadButton') }}</span>
            </v-btn>
            
            <!-- Materials History -->
            <MaterialsHistory @select="onMaterialSelected" class="mt-6" />
        </v-container>
        <div @click="openPopup(2)" class="inline-flex gap-1 justify-start items-center self-start my-3 h-4 md:my-4">
            <div class="relative w-4 h-4">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8.75 14.25C5.4362 14.25 2.75 11.5638 2.75 8.25C2.75 4.9362 5.4362 2.25 8.75 2.25C12.0638 2.25 14.75 4.9362 14.75 8.25C14.75 11.5638 12.0638 14.25 8.75 14.25ZM8.15 7.65V11.25H9.35V7.65H8.15ZM8.15 5.25V6.45H9.35V5.25H8.15Z" fill="#888A86" />
                </svg>
            </div>
            <div class="text-xs font-normal underline cursor-pointer grow shrink basis-0 text-zinc-600 md:text-base">
                {{ t('pages.faceSwap.tipsTitle') }}</div>
        </div>
    </template>
    <template v-else>
        <v-container class="!px-0" ref="dropZoneRef">
            <div class="flex flex-col items-stretch w-full lg:flex-row lg:items-start lg:justify-center" :class="{ 'mobile-container': isMobile }">
                <!-- 预览部分 -->
                <v-container class="px-0 rounded-2xl preview-container pb-2" :class="[{ 'my-auto': !facePickerVisible }, { 'lg:w-2/3 overflow-hidden': facePickerVisible }, { 'with-face-picker': facePickerVisible }]">
                    <!-- preview container -->
                    <div class="file-preview-container" :class="[{ '!bg-neutral-900 min-h-[574px]': facePickerVisible && !isMobile }]">
                        <!-- Back button when face picker is visible -->
                        <div v-if="facePickerVisible" class="absolute top-3 left-3 z-10">
                            <v-btn
                                @click="goBackToUpload"
                                icon
                                size="small"
                                class="bg-black/50 hover:bg-black/70"
                                style="border: 1px solid rgba(255, 255, 255, 0.2);"
                            >
                                <v-icon size="20" color="white">mdi-arrow-left</v-icon>
                            </v-btn>
                        </div>
                        <div v-if="mediaType === 'image'" class="rounded-2xl file-preview" :class="[{ 'md:w-auto': facePickerVisible }, { 'rounded-none': facePickerVisible && !isMobile }, orientation]">
                            <img  class="rounded-2xl" :src="resultUrl || previewUrl" alt="Image preview" referrerpolicy="no-referrer">
                            <!-- 根据不同的状态插入不同内容 -->
                            <!-- 大文件分块上传：仅毛玻璃，进度交给底部 UploadProgress -->
                            <StatusOverlay v-if="currStat === 'uploading' && isLargeUpload" :visible="true">
                            </StatusOverlay>

                            <StatusOverlay v-if="currStat === 'uploadFailed'" :visible="true">
                                <!-- 图片上传失败 -->
                                <div class="flex flex-col">
                                    <img class="w-16 h-16" src="/public/icon/img_failed.svg" alt="">
                                    <span class="ml-4 text-xs text-nuetral-300">Upload failed, please upload a new
                                        file
                                        again.</span>
                                </div>
                            </StatusOverlay>

                            <!-- 上传(小文件)/分析/分析完成：进度与提示统一覆盖在模糊图上 -->
                            <StatusOverlay v-else-if="currStat === 'analyzing' || currStat === 'analyzingSuccess' || (currStat === 'uploading' && !isLargeUpload)" :visible="true">
                                <div class="flex flex-col items-center w-full max-w-[420px] px-4 gap-3">
                                    <div class="flex items-center gap-2">
                                        <img class="w-7 h-7" src="/public/icon/magic.svg" alt="">
                                        <span class="text-3xl font-extrabold text-white">{{ Math.round(progress) }}%</span>
                                        <img class="w-7 h-7" src="/public/icon/magic.svg" alt="">
                                    </div>
                                    <span class="text-sm font-semibold text-white text-center">
                                        {{ currStat === 'analyzing' ? 'Analyze your images' : (currStat === 'analyzingSuccess' ? 'Your image is uploaded' : 'Your image is uploading…') }}
                                    </span>
                                    <div class="w-full">
                                        <v-progress-linear color="#F06906" :height="isMobile ? 6 : 8" :model-value="progress" rounded :class="{ 'no-transition': !enableTransition }"></v-progress-linear>
                                    </div>
                                    <span class="text-xs font-medium text-neutral-400 text-center">
                                        {{ currStat === 'analyzing' ? 'Please wait, this may take some patience.' : 'Create your project...' }}
                                    </span>
                                </div>
                            </StatusOverlay>

            <StatusOverlay v-else-if="currStat === 'creating'" :visible="true">
                                <!-- 创建中：进度/提示/按钮全部覆盖在模糊图上（移动端友好，无固定高度） -->
                                <div class="flex flex-col items-center w-full max-w-[420px] px-4 gap-3">
                                    <img src="/public/icon/creating.svg" alt="" class="w-16 h-16 animate-pulse">
                                    <span class="text-sm font-semibold text-white">Creating... {{ Math.round(progress) }}%</span>
                                    <div class="w-full">
                                        <v-progress-linear color="#F06906" :height="isMobile ? 6 : 8" :model-value="progress" rounded :class="{ 'no-transition': !enableTransition }"></v-progress-linear>
                                    </div>
                                    <div class="w-full text-neutral-400 text-xs font-medium text-center">
                                        Please wait, longer videos take more time.
                                    </div>
                                    <div class="flex flex-col sm:flex-row gap-3 w-full mt-1">
                                        <v-btn @click="goToMyCreations" class="bg-[#F06906]/20 text-[#f06906] flex-1">Wait in Creations</v-btn>
                                        <v-btn @click="router.go(0)" class="bg-[#f06906] text-black flex-1">Upload New</v-btn>
                                    </div>
                                </div>
                            </StatusOverlay>
                            <StatusOverlay v-else-if="currStat === 'creatingFailed' || currStat === 'analyzingFailed'"
                                :visible="true">
                                <!-- 图片创建失败 -->
                                <div class="flex flex-col items-center rounded-xl">
                                    <img class="w-16 h-16" src="/public/icon/img_failed.svg" alt="">
                                    <span class="ml-4 text-sm text-nuetral-300">{{ currStat === 'creatingFailed' ? 'Creation' : 'Analyzing' }} failed, please try
                                        again.</span>
                                </div>
                            </StatusOverlay>

                            <!-- switch face / re-create button -->
                            <div v-if="!isMobile && currStat === 'creatingSuccess'" class="absolute top-2 right-2 cursor-pointer face-change" @click="onReCreate">
                                <template v-if="currentSwapMode === 'single'">
                                    <!-- 单人模式：显示原始人脸和切换图标 -->
                                    <img :src="originalFace || '/public/images/nobody.svg'" class="w-12 h-12 rounded-full border-2 border-[#F06906]" alt="">
                                    <img src="/public/icon/switch.svg" class="absolute right-0 bottom-0 w-5 h-5" alt="">
                                </template>
                                <template v-else>
                                    <!-- 多人模式：显示重新创建图标 -->
                                    <div class="w-12 h-12 rounded-full bg-[#F06906] flex items-center justify-center">
                                        <v-icon size="28" color="black">mdi-refresh</v-icon>
                                    </div>
                                </template>
                            </div>
                        </div>

                        <div v-else-if="mediaType === 'video'" class="rounded-2xl file-preview" :class="[{ 'md:w-full': facePickerVisible }, { 'rounded-none': facePickerVisible && !isMobile }, orientation]">
                            <video :src="resultUrl || previewUrl" controls width="100%">
                            </video>

                            <!-- 根据不同的状态插入不同内容 -->
                            <!-- 大文件分块上传：仅毛玻璃，进度交给底部 UploadProgress -->
                            <StatusOverlay v-if="currStat === 'uploading' && isLargeUpload" :visible="true">
                            </StatusOverlay>

                            <StatusOverlay v-if="currStat === 'uploadFailed'" :visible="true">
                                <!-- 视频上传失败 -->
                                <div class="flex flex-col items-center">
                                    <img class="w-16 h-16" src="/public/icon/video_failed.svg" alt="">
                                    <span class="ml-4 text-xs text-nuetral-300">Upload failed, please upload a new
                                        file
                                        again.</span>
                                </div>
                            </StatusOverlay>

                            <!-- 上传(小文件)/分析/分析完成：进度与提示统一覆盖在模糊图上 -->
                            <StatusOverlay v-else-if="currStat === 'analyzing' || currStat === 'analyzingSuccess' || (currStat === 'uploading' && !isLargeUpload)" :visible="true">
                                <div class="flex flex-col items-center w-full max-w-[420px] px-4 gap-3">
                                    <div class="flex items-center gap-2">
                                        <img class="w-7 h-7" src="/public/icon/magic.svg" alt="">
                                        <span class="text-3xl font-extrabold text-white">{{ Math.round(progress) }}%</span>
                                        <img class="w-7 h-7" src="/public/icon/magic.svg" alt="">
                                    </div>
                                    <span class="text-sm font-semibold text-white text-center">
                                        {{ currStat === 'analyzing' ? 'Analyze your videos' : (currStat === 'analyzingSuccess' ? 'Your video is uploaded' : 'Your video is uploading…') }}
                                    </span>
                                    <div class="w-full">
                                        <v-progress-linear color="#F06906" :height="isMobile ? 6 : 8" :model-value="progress" rounded :class="{ 'no-transition': !enableTransition }"></v-progress-linear>
                                    </div>
                                    <span class="text-xs font-medium text-neutral-400 text-center">
                                        {{ currStat === 'analyzing' ? 'Please wait, this may take some patience.' : 'Create your project...' }}
                                    </span>
                                </div>
                            </StatusOverlay>

                            <StatusOverlay v-else-if="currStat === 'creating'" :visible="true">
                                <!-- 创建中：进度/提示/按钮全部覆盖在模糊图上（移动端友好，无固定高度） -->
                                <div class="flex flex-col items-center w-full max-w-[420px] px-4 gap-3">
                                    <img src="/public/icon/creating.svg" alt="" class="w-16 h-16 animate-pulse">
                                    <span class="text-sm font-semibold text-white">Creating... {{ Math.round(progress) }}%</span>
                                    <div class="w-full">
                                        <v-progress-linear color="#F06906" :height="isMobile ? 6 : 8" :model-value="progress" rounded :class="{ 'no-transition': !enableTransition }"></v-progress-linear>
                                    </div>
                                    <div class="w-full text-neutral-400 text-xs font-medium text-center">
                                        Please wait, longer videos take more time.
                                    </div>
                                    <div class="flex flex-col sm:flex-row gap-3 w-full mt-1">
                                        <v-btn @click="goToMyCreations" class="bg-[#F06906]/20 text-[#f06906] flex-1">Wait in Creations</v-btn>
                                        <v-btn @click="router.go(0)" class="bg-[#f06906] text-black flex-1">Upload New</v-btn>
                                    </div>
                                </div>
                            </StatusOverlay>

                            <StatusOverlay v-else-if="currStat === 'creatingFailed' || currStat === 'analyzingFailed'"
                                :visible="true">
                                <!-- 视频创建失败 -->
                                <div class="flex flex-col items-center">
                                    <img class="w-16 h-16" src="/public/icon/video_failed.svg" alt="">
                                    <span class="ml-4 text-xs text-nuetral-300">{{ currStat === 'creatingFailed' ?
                                        'Creation' : 'Analyzing' }} failed, please try
                                        again.</span>
                                </div>
                            </StatusOverlay>

                            <!-- switch face / re-create button -->
                            <div v-if="!isMobile && currStat === 'creatingSuccess'" class="absolute top-2 right-2 cursor-pointer face-change" @click="onReCreate">
                                <template v-if="currentSwapMode === 'single'">
                                    <!-- 单人模式：显示原始人脸和切换图标 -->
                                    <img :src="originalFace || '/public/images/nobody.svg'" class="w-12 h-12 rounded-full border-2 border-[#F06906]" alt="">
                                    <img src="/public/icon/switch.svg" class="absolute right-0 bottom-0 w-5 h-5" alt="">
                                </template>
                                <template v-else>
                                    <!-- 多人模式：显示重新创建图标 -->
                                    <div class="w-12 h-12 rounded-full bg-[#F06906] flex items-center justify-center">
                                        <v-icon size="28" color="black">mdi-refresh</v-icon>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                    <!-- bottom -->
                    <template v-if="!facePickerVisible">
                        <div class="actions flex items-center justify-center my-8 flex-col md:flex-row px-3 md:px-0 mx-auto max-w-[740px]">
                            <template v-if="currStat == 'creatingSuccess'">
                                <div class="flex mb-4 w-full row md:mb-0 md:w-auto">
                                    <v-btn @click="router.go(0)" prepend-icon="mdi-cloud-upload" style="background: rgba(240, 105, 6, 0.20) !important" class="bg-[#FFFFFF1A] px-2 md:px-6 mr-2  md:mr-6 flex-grow md:flex-grow-0 text-[#F06906] w-64 lg:w-[346px]">
                                        Upload File</v-btn>
                                    <!-- 移动端人脸图标 / 重新创建按钮 -->
                                    <div v-if="isMobile" class="relative face-change" @click="onReCreate">
                                        <template v-if="currentSwapMode === 'single'">
                                            <!-- 单人模式：显示原始人脸和切换图标 -->
                                            <img :src="originalFace || '/public/images/nobody.svg'" class="w-12 h-12 rounded-full border-2 border-[#F06906]" alt="">
                                            <img src="/public/icon/switch.svg" class="absolute right-0 bottom-0 w-5 h-5" alt="">
                                        </template>
                                        <template v-else>
                                            <!-- 多人模式：显示重新创建图标 -->
                                            <div class="w-12 h-12 rounded-full bg-[#F06906] flex items-center justify-center">
                                                <v-icon size="28" color="black">mdi-refresh</v-icon>
                                            </div>
                                        </template>
                                    </div>
                                </div>
                                <div class="flex mb-4 w-full row md:mb-0 md:mr-6 md:w-auto">
                                    <v-btn @click="utils.download(resultUrl)" prepend-icon="mdi-cloud-download" class="bg-[#F06906] text-black flex-1 mr-2 md:w-64 lg:w-[346px]">
                                        Download</v-btn>
                                    <v-btn icon @click="utils.copyToClipboard(resultUrl)" class="w-12 h-12 rounded-full bg-neutral-800">
                                        <img class="w-6 h-6" src="/public/icon/file-copy-fill.svg" alt="Copy" />
                                    </v-btn>
                                </div>
                            </template>
                            <template v-if="showAction">
                                <!-- 大文件分块上传仍用 UploadProgress（暂停/续传）；
                                     小文件上传/分析/分析完成的进度与提示已挪到模糊图 overlay -->
                                <div v-if="currStat === 'uploading' && isLargeUpload" class="w-full">
                                    <UploadProgress
                                        :file-name="uploadStore.fileName"
                                        :file-size="uploadStore.fileSize"
                                        :chunks="uploadStore.chunks"
                                        :total-chunks="uploadStore.totalChunks"
                                        :overall-progress="uploadStore.overallProgress"
                                        :upload-status="uploadStore.uploadStatus"
                                        @pause="pauseUpload"
                                        @resume="resumeUpload"
                                        @retry="retryUpload"
                                    />
                                </div>
                                <div v-if="currStat === 'creatingFailed'" class="w-full max-w-96">
                                    <v-btn @click="onReCreate" class="w-full text-black bg-gradient-to-bl from-amber-500 to-orange-600">
                                        Re-Create
                                    </v-btn>
                                </div>
                                <div v-if="currStat === 'uploadFailed' || currStat === 'analyzingFailed'" class="w-full max-w-96">
                                    <v-btn @click="openFile()" color="primary" class="mx-auto mt-6 w-full" size="large" style="color: #000 !important;" prepend-icon="mdi-cloud-upload">
                                        <span>Upload File</span>
                                    </v-btn>
                                </div>
                                <!-- creating 的进度/提示/按钮已挪到模糊图上的 StatusOverlay，此处不再单独展示 -->
                            </template>
                        </div>

                        <!-- V2.0 用户反馈组件 - 仅在换脸成功时显示 -->
                        <ResultFeedbackForm
                            v-if="currStat === 'creatingSuccess' && currentSwapMode === 'multiple' && showFeedbackForm"
                            :task-id="currentTaskId"
                            :order-id="currentOrderId"
                            :result-url="resultUrl"
                            :auto-fill-email="true"
                            @submitted="onFeedbackSubmitted"
                            @close="onFeedbackClose"
                        />
                    </template>
                </v-container>
                <!-- 选脸部分 -->
                <template v-if="facePickerVisible && faceDetectData">
                      <FacePicker id="face-picker" class="relative px-0 pb-6 mt-3 rounded-t-2xl bg-neutral-900 md:rounded-b-2xl face-picker md:ml-6 md:mt-0" :class="{ 'lg:w-1/3 overflow-hidden': facePickerVisible }" :face-detect-data="faceDetectData" :credit-num="creditNum" :free-remaining="freeRemaining" :tool-id="tool" @start-polling="pollFaceSwapResult">
                    </FacePicker>
                </template>
            </div>
        </v-container>
    </template>
    <Dialog v-model="dialogVisible" :title="dialogTitle" :message="dialogMessage" :show-icon="dialogShowIcon">
    </Dialog>
    <TipsDialog v-model="tipsDialogVisible" :show-bottom-btn="showBottomBtn" @confirm="handleTipsDialogConfirm" />
    <VideoEditorDialog v-model="isShowVideoEditorDialog" :video-file="selectedFile" :show-close-btn="true" @confirm-trim-end="handleVideoConfirmTrim" @on-close="handleVideoEditorClose" />
</template>
<style lang="scss" scoped>
.preview-container .file-preview {
    position: relative;
    // margin: 20px auto;
    text-align: center;
    // background-color: #111;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    // height: auto;
}

.portrait {
    // width: 400px;
    width: auto;
    height: 584px !important;
    object-fit: cover;
    display: flex;
    align-items: center;

}

.portrait img,
.portrait video {
    margin: auto auto;
    height: 100%;
    position: relative;
}

.landscape {
    width: 740px !important;
    // height: 444px;
    object-fit: cover;
    object-position: center;
}

@media (max-width: 768px) {
    /* 生成/结果预览（无 FacePicker）移动端不要贴屏幕边缘，留出左右边距 */
    .preview-container:not(.with-face-picker) {
        padding-left: 1rem !important;
        padding-right: 1rem !important;
    }

    /* 只在有 FacePicker 时，预览容器占据剩余空间 */
    .preview-container.with-face-picker {
        flex: 1 !important;
        min-height: 0 !important;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow-y: auto;  /* 允许垂直滚动 */
        overflow-x: hidden;
    }

    /* 只在有 FacePicker 时，素材容器填充父容器 */
    .preview-container.with-face-picker .file-preview-container {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    /* 只在有 FacePicker 时，素材自适应容器大小 */
    .preview-container.with-face-picker .portrait {
        max-height: 100% !important;
        max-width: 100% !important;
        height: 100% !important;
        width: 100% !important;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .preview-container.with-face-picker .portrait img,
    .preview-container.with-face-picker .portrait video {
        max-height: 100%;
        max-width: 100%;
        height: auto !important;
        width: auto !important;
        object-fit: contain;
    }

    .preview-container.with-face-picker .landscape {
        max-height: 100% !important;
        max-width: 100% !important;
        height: 100% !important;
        width: 100% !important;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .preview-container.with-face-picker .landscape img,
    .preview-container.with-face-picker .landscape video {
        max-height: 100%;
        max-width: 100%;
        height: auto !important;
        width: auto !important;
        object-fit: contain;
    }
}

.file-preview-container {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
}

.overlay-blur {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(10px);
    z-index: 5;
    display: flex;
    align-items: center;
    justify-content: center;
}

/* 蒙层样式 */
.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
}

.loading-text {
    margin-top: 10px;
    color: white;
    font-size: 16px;
}

@media (max-width: 768px) {
    .face-picker {
        width: 100vw;
        position: relative;
        left: 45.5%;
        transform: translateX(-46%);
        background-color: #000;
        flex-shrink: 0;
        max-height: 60vh;
        overflow-y: auto;
    }
}

.mobile-container {
    position: fixed;
    z-index: 21;
    background-color: black;
    left: 0;
    right: 0;
    bottom: 0;
    top: 80px;
    overflow-y: auto;  /* 允许整个容器垂直滚动 */
    overflow-x: hidden;
}

.mobile-container .preview-container {
    overflow-y: visible !important;  /* 移动端取消preview-container的overflow限制 */
    overflow-x: hidden;
}

.mobile-container .file-preview {
    overflow: visible !important;  /* 确保预览区域不会隐藏溢出内容 */
}

/* 添加拖拽相关样式 */
.border-primary {
    border-color: #F06906 !important;
    border-width: 3px !important;
}

/* 禁用进度条过渡动画 - 用于避免重置进度时的回退动画 */
.no-transition :deep(.v-progress-linear__determinate),
.no-transition :deep(.v-progress-linear__indeterminate) {
    transition: none !important;
}
</style>